import React, { useState, useEffect } from 'react';
import './SurveyModal.css';
import { API_BASE } from '../../DatabaseAPI';

const SurveyModal = ({ onClose, onSubmit }) => {
  const [surveyData, setSurveyData] = useState(null);
  const [responses, setResponses] = useState({});
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchActiveSurvey();
  }, []);

  const fetchActiveSurvey = async () => {
    try {
      const response = await fetch(`${API_BASE}/v2/survey/active/`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
        }
      });

      console.log('Survey fetch response status:', response.status);
      console.log('Survey fetch response headers:', response.headers.get('content-type'));
      
      if (response.ok) {
        const responseText = await response.text();
        console.log('Survey raw response:', responseText);
        
        try {
          const data = JSON.parse(responseText);
          console.log('Survey API response:', data);
          
          if (data.survey) {
            setSurveyData(data.survey);
            // Initialize responses object with null values
            const initialResponses = {};
            data.survey.questions.forEach((_, index) => {
              initialResponses[index] = null;
            });
            setResponses(initialResponses);
          } else {
            // No active survey or user already responded, close modal
            console.log('No active survey or already responded');
            onClose();
          }
        } catch (parseError) {
          console.error('Error parsing JSON:', parseError);
          console.error('Response was:', responseText);
          onClose();
        }
      } else {
        const errorText = await response.text();
        console.error('Failed to fetch survey, status:', response.status, 'Response:', errorText);
        onClose();
      }
    } catch (error) {
      console.error('Error fetching survey:', error);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleRadioChange = (questionIndex, value) => {
    setResponses(prev => ({
      ...prev,
      [questionIndex]: value
    }));
    // Clear error for this question
    setErrors(prev => ({
      ...prev,
      [questionIndex]: false
    }));
  };

  const handleSubmit = async () => {
    // Validate all questions are answered
    const newErrors = {};
    let hasErrors = false;

    surveyData.questions.forEach((_, index) => {
      if (responses[index] === null) {
        newErrors[index] = true;
        hasErrors = true;
      }
    });

    if (hasErrors) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      // Get CSRF token from cookie
      const getCookie = (name) => {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop().split(';').shift();
      };
      
      const csrftoken = getCookie('csrftoken');
      
      const response = await fetch(`${API_BASE}/v2/survey/submit/`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrftoken
        },
        body: JSON.stringify({
          survey_id: surveyData.id,
          responses: responses
        })
      });

      if (response.ok) {
        console.log('Survey submitted successfully');
        if (onSubmit) {
          onSubmit();
        }
        onClose();
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('Failed to submit survey:', errorData);
        alert('Failed to submit survey. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting survey:', error);
      alert('Error submitting survey. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="survey-modal-overlay">
        <div className="survey-modal">
          <div className="survey-loading">Loading survey...</div>
        </div>
      </div>
    );
  }

  if (!surveyData) {
    return null;
  }

  return (
    <div className="survey-modal-overlay">
      <div className="survey-modal">
        <button className="survey-close-button" onClick={onClose}>×</button>
        
        <div className="survey-content">
          <div className="survey-instructions">
            Rate each statement from 1-5 where 1 is "Strongly Disagree" and 5 is "Strongly Agree"
          </div>
          
          <div className="survey-header">
            <div className="survey-labels">
              <span className="survey-label-spacer"></span>
              <div className="survey-rating-labels">
                <span>1</span>
                <span>2</span>
                <span>3</span>
                <span>4</span>
                <span>5</span>
              </div>
            </div>
          </div>

          <div className="survey-questions">
            {surveyData.questions.map((question, index) => (
              <div 
                key={index} 
                className={`survey-row ${errors[index] ? 'survey-row-error' : ''}`}
              >
                <div className="survey-question-text">{question}</div>
                <div className="survey-radio-group">
                  {[1, 2, 3, 4, 5].map(value => (
                    <label key={value} className="survey-radio-label">
                      <input
                        type="radio"
                        name={`question-${index}`}
                        value={value}
                        checked={responses[index] === value}
                        onChange={() => handleRadioChange(index, value)}
                        className="survey-radio-input"
                      />
                      <span className="survey-radio-custom"></span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <button
            className="survey-submit-button"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : (
              <>
                Submit <span className="mythril-reward">💎 +10</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SurveyModal;